import { useEffect, useRef, useState, useCallback } from "react";
import type { ApiClient } from "../api/client";

export type PlaybackState = "idle" | "loading" | "playing" | "error";

interface UseVoiceOutputResult {
  playbackState: PlaybackState;
  playingMessageId: string | null;
  stopPlayback: () => void;
  speakText: (text: string, messageId: string) => void;
  unlockAudio: () => void;
}

const SILENT_WAV =
  "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";

const MAX_AUDIO_CACHE_ENTRIES = 10;
export const audioCache = new Map<string, ArrayBuffer>();

function cacheGet(messageId: string): ArrayBuffer | undefined {
  const buffer = audioCache.get(messageId);
  if (buffer !== undefined) {
    audioCache.delete(messageId);
    audioCache.set(messageId, buffer);
  }
  return buffer;
}

function cacheSet(messageId: string, buffer: ArrayBuffer): void {
  if (audioCache.has(messageId)) {
    audioCache.delete(messageId);
  }
  audioCache.set(messageId, buffer);
  if (audioCache.size > MAX_AUDIO_CACHE_ENTRIES) {
    const oldest = audioCache.keys().next().value;
    if (oldest !== undefined) {
      audioCache.delete(oldest);
    }
  }
}

export function useVoiceOutput(
  apiClient: ApiClient,
  onPlaybackEnded?: () => void,
): UseVoiceOutputResult {
  const [playbackState, setPlaybackState] = useState<PlaybackState>("idle");
  const [playingMessageId, setPlayingMessageId] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const userStoppedRef = useRef(false);
  const onPlaybackEndedRef = useRef<(() => void) | undefined>(onPlaybackEnded);

  useEffect(() => {
    onPlaybackEndedRef.current = onPlaybackEnded;
  }, [onPlaybackEnded]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  function getAudio(): HTMLAudioElement {
    if (!audioRef.current) {
      audioRef.current = new Audio();
    }
    return audioRef.current;
  }

  const revokeBlobUrl = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  const teardownPlayback = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    const audio = getAudio();
    audio.pause();
    audio.src = "";
    revokeBlobUrl();
    setPlaybackState("idle");
    setPlayingMessageId(null);
  }, [revokeBlobUrl]);

  const stopPlayback = useCallback(() => {
    userStoppedRef.current = true;
    teardownPlayback();
  }, [teardownPlayback]);

  // Safari blocks autoplay when play() is called after an async gap from the user gesture.
  // We keep one persistent HTMLAudioElement and call unlockAudio() synchronously inside
  // enterConversationMode (a direct user gesture), then reuse that same element for all
  // TTS playback so Safari's autoplay policy is satisfied.
  const unlockAudio = useCallback(() => {
    const audio = getAudio();
    audio.src = SILENT_WAV;
    audio.play().catch(() => {});
  }, []);

  const playBuffer = useCallback(
    (buffer: ArrayBuffer, controller: AbortController, messageId: string) => {
      if (controller.signal.aborted) return;

      revokeBlobUrl();
      const blob = new Blob([buffer], { type: "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;

      const audio = getAudio();
      audio.src = url;

      audio.addEventListener(
        "playing",
        () => {
          setPlaybackState("playing");
        },
        { signal: controller.signal },
      );

      audio.addEventListener(
        "ended",
        () => {
          revokeBlobUrl();
          setPlaybackState("idle");
          setPlayingMessageId(null);
          const wasStopped = userStoppedRef.current;
          userStoppedRef.current = false;
          if (!wasStopped) {
            onPlaybackEndedRef.current?.();
          }
        },
        { signal: controller.signal },
      );

      audio.addEventListener(
        "error",
        () => {
          revokeBlobUrl();
          setPlaybackState("idle");
          setPlayingMessageId(null);
        },
        { signal: controller.signal },
      );

      setPlayingMessageId(messageId);
      audio.play().catch(() => {
        if (!mountedRef.current) return;
        revokeBlobUrl();
        setPlaybackState("idle");
        setPlayingMessageId(null);
      });
    },
    [revokeBlobUrl],
  );

  const speakText = useCallback(
    (text: string, messageId: string) => {
      if (!text.trim()) return;

      userStoppedRef.current = false;

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const audio = getAudio();
      audio.pause();
      audio.src = "";
      revokeBlobUrl();

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setPlaybackState("loading");
      setPlayingMessageId(messageId);

      const cached = cacheGet(messageId);
      if (cached !== undefined) {
        playBuffer(cached, controller, messageId);
        return;
      }

      apiClient
        .speakText(text, controller.signal, messageId)
        .then((buffer) => {
          if (controller.signal.aborted) return;
          cacheSet(messageId, buffer);
          playBuffer(buffer, controller, messageId);
        })
        .catch((err) => {
          if (err instanceof Error && err.name === "AbortError") return;
          setPlayingMessageId(null);
          setPlaybackState("error");
          setTimeout(() => {
            if (mountedRef.current) setPlaybackState("idle");
          }, 3000);
        });
    },
    [apiClient, revokeBlobUrl, playBuffer],
  );

  useEffect(() => {
    return () => {
      teardownPlayback();
    };
  }, [teardownPlayback]);

  return {
    playbackState,
    playingMessageId,
    stopPlayback,
    speakText,
    unlockAudio,
  };
}
